import React from 'react';
import { Handle, Position, NodeProps, Node } from '@xyflow/react';
import { Box, Typography } from '@mui/material';
import { useTheme, decomposeColor, recomposeColor } from '@mui/material/styles';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { WorkflowNodeKind, NodeVariant, Side } from '../../workflow/workflowConfig';
import { GateInlet, CHIP_H } from './workflowLayout';

export interface WorkflowNodeData extends Record<string, unknown> {
  variant: NodeVariant;
  label: string;        // the diagram label ('' for count-only nodes)
  fullLabel: string;    // the full label, used for the count node's aria-label
  count: number;
  priorityCount: number;
  isSelected: boolean;
  kind: WorkflowNodeKind;
  failure: boolean;
  // Gate-only: one target handle per incoming lane, plus the right outlet to Merged.
  inlets?: GateInlet[];
  outletOffsetY?: number;
}

export type WorkflowFlowNode = Node<WorkflowNodeData, 'workflow'>;

// Palette per lane kind so the diagram reads at a glance: parked=neutral, review=blue,
// terminal=green/purple, unit=orange, scoped tests=teal.
const KIND_COLOR: Record<WorkflowNodeKind, string> = {
  parked: '#9e9e9e',
  review: '#1976d2',
  terminal: '#2e7d32',
  unit: '#ed6c02',
  test: '#0097a7',
  group: '#9e9e9e',
};

// Failure side-states use one alarming tone regardless of lane, so a teal test lane's failures read
// as urgent as the orange unit lane's rather than a calm teal.
const FAILURE_COLOR = '#ed6c02';

// The "needs" queue boxes likewise use one tone regardless of lane: a queue is a to-do, not an
// alarm, and the unit lane's orange is the same hue as FAILURE_COLOR, so an orange needs-unit badge
// read as a failure next to the teal needs-recompute / needs-e2e badges.
const QUEUE_COLOR = KIND_COLOR.test;

// A chip's count badge, sized to leave a hairline of paper between the badge and the chip's 1px
// border. Filling the full CHIP_H made the badge read as a solid block wedged into the chip.
const CHIP_BADGE_SIZE = CHIP_H - 4;

// How strongly the accent tints a node's fill when it is selected, hovered, and pressed.
const SELECTED_TINT = 0.08;
const HOVER_TINT = 0.12;
const ACTIVE_TINT = 0.2;

// Mix `accent` into `base` and return an opaque color. The selected/hover fills have to be opaque
// rather than a translucent accent over `background.paper`: the parked nodes sit on top of the
// shaded Parked panel, so a see-through fill picks up the panel's shade and the selected node
// dissolves into the panel instead of reading as a highlighted box.
function tintOverPaper(base: string, accent: string, weight: number): string {
  const b = decomposeColor(base).values;
  const a = decomposeColor(accent).values;
  return recomposeColor({
    type: 'rgb',
    values: [
      Math.round(b[0] + (a[0] - b[0]) * weight),
      Math.round(b[1] + (a[1] - b[1]) * weight),
      Math.round(b[2] + (a[2] - b[2]) * weight),
    ],
  });
}

const SIDE_TO_POSITION: Record<Side, Position> = {
  left: Position.Left,
  right: Position.Right,
  top: Position.Top,
  bottom: Position.Bottom,
};

const ALL_SIDES: Side[] = ['left', 'right', 'top', 'bottom'];

// Invisible source+target handles on all four sides; edges pick a side by handle id (s-<side> /
// t-<side>). The graph is read-only so they are never draggable.
const SideHandles: React.FC = () => (
  <>
    {ALL_SIDES.map(side => (
      <React.Fragment key={side}>
        <Handle id={'t-' + side} type="target" position={SIDE_TO_POSITION[side]} style={{ opacity: 0 }} isConnectable={false} />
        <Handle id={'s-' + side} type="source" position={SIDE_TO_POSITION[side]} style={{ opacity: 0 }} isConnectable={false} />
      </React.Fragment>
    ))}
  </>
);

// The count pill shown on boxes and chips (and standalone on count nodes). A zero badge is noise on
// a labelled node — the node's faded fill already says "nothing here" — so it is dropped. Count-only
// nodes pass `showZero` because the badge is their entire content; without it they'd be blank boxes.
const CountBadge: React.FC<{ count: number; accent: string; size: number; showZero?: boolean }> = ({ count, accent, size, showZero }) => {
  const empty = count === 0;
  if (empty && !showZero) return null;
  const small = size < 18;
  return (
    <Box
      sx={{
        flexShrink: 0,
        minWidth: size,
        height: size,
        px: small ? 0.25 : 0.5,
        borderRadius: size / 2 + 'px',
        bgcolor: empty ? 'action.disabledBackground' : accent,
        color: empty ? 'text.disabled' : '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: small ? 10 : 12,
        fontWeight: 700,
      }}
    >
      {count}
    </Box>
  );
};

const PriorityBadge: React.FC<{ priorityCount: number }> = ({ priorityCount }) => {
  if (priorityCount <= 0) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        top: -8,
        left: -8,
        minWidth: 16,
        height: 16,
        px: 0.25,
        borderRadius: '8px',
        bgcolor: 'error.main',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 700,
      }}
      aria-label={`${priorityCount} priority PR${priorityCount > 1 ? 's' : ''}`}
    >
      {priorityCount === 1 ? <PriorityHighIcon sx={{ fontSize: 11 }} /> : priorityCount}
    </Box>
  );
};

// MUI-flat node: a 1px divider border, no shadow, and a background.paper fill so boxes stand out on
// the page's gray background. Renders as a box, a chip (smaller side state), a count-only badge, or
// the tall Ready gate every lane funnels into.
const WorkflowNode: React.FC<NodeProps<WorkflowFlowNode>> = ({ data }) => {
  const { variant, label, fullLabel, count, priorityCount, isSelected, kind, failure, inlets, outletOffsetY } = data;
  const accent = failure ? FAILURE_COLOR : variant === 'count' ? QUEUE_COLOR : (KIND_COLOR[kind] ?? '#1976d2');
  const empty = count === 0;
  const paper = useTheme().palette.background.paper;

  const baseSx = {
    position: 'relative' as const,
    width: '100%',
    height: '100%',
    boxSizing: 'border-box' as const,
    border: '1px solid',
    borderColor: isSelected ? accent : 'divider',
    borderRadius: 1,
    bgcolor: isSelected ? tintOverPaper(paper, accent, SELECTED_TINT) : paper,
    opacity: empty && !isSelected ? 0.5 : 1,
    cursor: 'pointer',
    transition: 'opacity 120ms, border-color 120ms, background-color 120ms',
    display: 'flex',
    alignItems: 'center',
    // Hover signals the node is clickable (a slightly deeper accent tint, and full opacity so an
    // empty/faded node no longer looks disabled while pointed at); :active deepens the tint to
    // acknowledge the press. On release the box settles back to its resting state (the selected
    // tint or plain paper) via the transition above.
    '&:hover': {
      bgcolor: tintOverPaper(paper, accent, HOVER_TINT),
      opacity: 1,
    },
    '&:active': {
      bgcolor: tintOverPaper(paper, accent, ACTIVE_TINT),
    },
  };

  if (variant === 'gate') {
    return (
      <Box sx={{ ...baseSx, flexDirection: 'column', justifyContent: 'center', gap: 0.25, px: 0.5 }}>
        {inlets?.map(inlet => (
          <Handle key={inlet.id} id={inlet.id} type="target" position={Position.Left} style={{ top: inlet.offsetY, opacity: 0 }} isConnectable={false} />
        ))}
        <Handle id="out-merged" type="source" position={Position.Right} style={{ top: outletOffsetY, opacity: 0 }} isConnectable={false} />
        <PriorityBadge priorityCount={priorityCount} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.1, color: 'text.primary' }}>{label}</Typography>
        <CountBadge count={count} accent={accent} size={18} />
      </Box>
    );
  }

  if (variant === 'count') {
    // A label-less queue badge; its meaning comes from placement (under Open) and the stage
    // explanation shown in the detail panel when it's selected.
    return (
      <Box sx={{ ...baseSx, justifyContent: 'center' }} aria-label={fullLabel}>
        <SideHandles />
        <PriorityBadge priorityCount={priorityCount} />
        <CountBadge count={count} accent={accent} size={18} showZero />
      </Box>
    );
  }

  const isChip = variant === 'chip';
  return (
    <Box sx={{ ...baseSx, justifyContent: 'space-between', gap: 0.5, px: isChip ? 0.5 : 0.75 }}>
      <SideHandles />
      <PriorityBadge priorityCount={priorityCount} />
      <Typography
        noWrap
        // Chips are half the height of a box, so a bold label makes them look crammed; regular
        // weight gives the short side-state labels room to breathe.
        sx={{ fontSize: isChip ? 11 : 12, fontWeight: isChip ? 400 : 600, lineHeight: 1.1, color: 'text.primary' }}
      >
        {label}
      </Typography>
      <CountBadge count={count} accent={accent} size={isChip ? CHIP_BADGE_SIZE : 18} />
    </Box>
  );
};

export default WorkflowNode;
